import { Company, CompanyRole } from "../types/CompanyInfo";
import { RoleType } from "../types/enums/RoleType";
import { isEmpty, isFinite, truncate } from "lodash";
import {
  parsePhoneNumber,
  phoneNumberValidationMessages,
  validatePhoneNumber,
} from "./phoneNumbers";
import { dnr, fnr, idnr } from "@navikt/fnrvalidator";

import { TMessage } from "../i18n/tt";
import { isSwedishOrgNo } from "./orgNo";
import { isValidDate } from "./Dates";

export const formatCompanyName = (fullCompanyName?: string): string => {
  if (fullCompanyName) {
    return truncate(fullCompanyName, { length: 25, omission: "..." });
  }
  return "";
};

export const formatOrgNum = (orgNum?: string): string =>
  orgNum?.replace(/(^(\d{3})(\d{3})(\d{3})$)/g, "$2 $3 $4") || "";

export const getCompanyCEO = (roles: CompanyRole[]) =>
  roles.find((role) => role.role === RoleType.CEO);

export const getCompanyNumber = (company: Company) =>
  company.organizationnumber.replace(/(\d{3})/g, "$1 ");

export const delay = (ms: number) => {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
};

export const isBase64 = (str: string) =>
  str.length % 4 === 0 && /^[A-Za-z0-9+/]+[=]{0,3}$/.test(str);

export const isJsonString = (str: string) => {
  try {
    JSON.parse(str);
  } catch {
    return false;
  }
  return true;
};

export function getWindowDimensions() {
  const { innerWidth: width, innerHeight: height } = window;
  return {
    width,
    height,
  };
}

export function isRequired(value: string) {
  return !value.trim();
}

export const personNameValidator = (value: string): TMessage | undefined => {
  const parts = value.split(" ");
  if (parts.length > 1 && parts.some((s) => s.trim().length > 0)) {
    return undefined;
  } else {
    return { key: "invalidPersonNameFormat" };
  }
};

export const contactInfoNameValidator = (value: string): TMessage | undefined => {
  const parts = value.split(" ").filter((s) => !isEmpty(s));
  if (parts.length === 0) {
    return undefined;
  }
  if (parts.length > 1 && parts.some((s) => s.trim().length > 0)) {
    return undefined;
  } else {
    return { key: "invalidPersonNameFormat" };
  }
};

export const phoneValidatorAllowingEmpty = (value: string): TMessage | undefined => {
  if (value.trim().length === 0) {
    return undefined;
  } else {
    return phoneValidator(value);
  }
};

export const phoneValidator = (value: string): TMessage | undefined => {
  const parsed = parsePhoneNumber(value);
  if (!parsed) {
    return { key: "phone-number.invalid-number" };
  }
  const validationError = validatePhoneNumber(parsed?.countryCode ?? "", parsed.value ?? "");
  if (!validationError) {
    return undefined;
  } else {
    return { key: phoneNumberValidationMessages[validationError] };
  }
};

export const utlNumberValidator = (value: string): TMessage | undefined => {
  if (value.trim().length !== 9 && value.trim().length !== 0) {
    return { key: "invalidUtlNumber" };
  } else {
    return undefined;
  }
};

export const norwegianSocialSecurityNumberValidator = (value: string): TMessage | undefined => {
  if (fnr(value.trim()).status === "valid") {
    return undefined;
  } else {
    return { key: "invalidSocialSecurityNumber" };
  }
};

export const dNumberValidator = (value: string): TMessage | undefined => {
  if (dnr(value.trim()).status === "valid") {
    return undefined;
  } else {
    return { key: "invalidDnr" };
  }
};

export const norwegianPersonalIdValidator = (value: string): TMessage | undefined => {
  if (idnr(value.trim()).status === "valid") {
    return undefined;
  } else {
    return { key: "invalidNorwegianNationalId" };
  }
};

export const swedishPersonalIdValidator = (value: string): TMessage | undefined => {
  const trimmed = value.trim();
  const isValid =
    isFinite(Number(trimmed)) && trimmed.length === 12 && isValidDate(trimmed.substring(0, 8));
  if (isValid) {
    return undefined;
  } else {
    return { key: "invalidSwedishNationalId" };
  }
};

export const nationalIdValidator = (orgNo: string) =>
  isSwedishOrgNo(orgNo) ? swedishPersonalIdValidator : norwegianPersonalIdValidator;

export function removeTitleAndTD(html: string) {
  return html
    .replace(/<div class="title">.*<\/div>\n/, "")
    .replace(/<td.*?>([\s\S]*)<\/td>/i, "$1");
}
